/**
 * Shared vocabulary for master-suite step outcomes.
 *
 * The runner writes more than Pass/Fail, and the extra words all mean "this step never
 * produced a result" — but for different reasons:
 *   Stopped    an older run halted the whole suite
 *   Skipped    an upstream step in THIS step's branch failed with stopIfFailed, so its
 *              inputs never arrived (fan-out: sibling branches are unaffected)
 *   Timed Out  the run's end-time cutoff fired before this step started
 *
 * Grouping them behind one predicate keeps every surface consistent: a step that did not
 * run must never be coloured or counted as a failure, and must never silently disappear
 * from a total (which is what happened to "Timed Out" before this existed — it matched
 * neither the pass, fail, nor stopped filter, so the step-count split didn't add up).
 */
export const NOT_RUN_STATUSES = ["Stopped", "Skipped", "Timed Out"];

/** True when the step never ran — see NOT_RUN_STATUSES for the three reasons. */
export const isNotRun = (status) => NOT_RUN_STATUSES.includes(status);

/** True when a run finished with some branches passing and others failing. */
export const isPartial = (status) => status === "Partial";

// ── The success / terminal vocabulary ────────────────────────────────────────
// ARC records ONE "this succeeded" concept under five spellings, because different writers grew up
// separately: "Completed" (job_status / run_status), "Pass" (compare test_result), "Success"
// (process-automation test_result), "Passed" (a label), and "Complete" — a legacy value with no
// writer left in the code but ~20 rows still in the database.
//
// Every `=== "Completed"` in the app is therefore a latent bug: it silently excludes the other four.
// Ask these instead of comparing strings. Mirrors constants/RunStatus.java on the backend — keep the
// two lists in step.

const norm = (s) => (typeof s === "string" ? s.trim().toLowerCase() : "");

const SUCCESS = ["Completed", "Complete", "Pass", "Passed", "Success"].map(norm);

const TERMINAL = [
  ...["Completed", "Complete", "Pass", "Passed", "Success"],
  ...["Error", "Fail", "Failed", "Completed with Error"],
  ...["Timed Out", "Stopped", "Skipped", "Partial"],
].map(norm);

/** True when the status means the work passed, whichever spelling was written. */
export const isSuccess = (status) => SUCCESS.includes(norm(status));

/**
 * True when the status is final, so a poller can stop.
 *
 * The counterpart matters more than the members: "In Progress", "Queued", "Running", "Started" and
 * anything unrecognised are NOT terminal. Treating an unknown status as non-terminal is deliberate —
 * a poller that keeps waiting is recoverable; one that stops early reports a result that never
 * happened.
 */
export const isTerminal = (status) => TERMINAL.includes(norm(status));
